/* Tela 7 · Certificados A1 (UC07): validade de cada empresa com a barra de tempo restante
   e, ao lado, o envio do .pfx com as quatro conferências (senha, CNPJ, validade, cifragem).
   A empresa passa a emitir quando o certificado dela é aceito.
   Fluxo alternativo (aba "Procuração"): a empresa emite com o certificado do escritório,
   que recebeu procuração NFS-e no e-CAC. O certificado próprio segue sendo o padrão. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  Eye,
  EyeSlash,
  Handshake,
  Lock,
  ShieldCheck,
  UploadSimple,
} from 'phosphor-react';
import { useSearchParams } from 'react-router-dom';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { useAuth } from '../../../../../providers/auth';
import {
  INfseCertificado,
  INfseEmitente,
  INfseEmpresaBusca,
} from '../../../../../types/nfseEmissao';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { NFSE_ACOES, NFSE_EMISSAO_PATH, NFSE_PATHS } from '../shared/constants';
import { EmpresaBusca } from '../shared/EmpresaBusca';
import { island } from '../shared/NfseIsland';
import { Carregando, NfsePage } from '../shared/NfsePage';
import { ProcuracaoForm } from './ProcuracaoForm';
import { Linha, SubLinha, Tabela, Td, Th, Vazio } from '../shared/tabela';
import {
  Banner,
  C,
  Card,
  CardHead,
  CheckItem,
  CheckList,
  cnpjMask,
  dataBR,
  EnvBadge,
  IconTile,
  Meter,
  ModuleHeader,
  NButton,
  StatusTag,
} from '../shared/ui';

const paraBusca = (e: INfseEmitente): INfseEmpresaBusca => ({
  empresa_id: e.empresa_id,
  external_id: '',
  razao_social: e.razao_social,
  cnpj: e.cnpj,
  municipio: e.municipio,
  uf: e.uf,
  emitente_id: e.id,
});

// Em qual das quatro conferências o servidor parou: vem em `detail.etapa`.
const ETAPAS: Record<string, number> = { senha: 1, cnpj: 2, validade: 3, cifragem: 4 };

function etapaComErro(err: any): number {
  return ETAPAS[err?.response?.data?.detail?.etapa] ?? 1;
}

function EnvioCertificado({
  emitentes,
  inicial,
}: {
  emitentes: INfseEmitente[];
  inicial: INfseEmpresaBusca | null;
}) {
  const queryClient = useQueryClient();
  const { enviarCertificado } = useNfseEmissao();
  const [empresa, setEmpresa] = useState<INfseEmpresaBusca | null>(inicial);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inicial) {
      setEmpresa(inicial);
      setArquivo(null);
      setSenha('');
    }
  }, [inicial]);

  const mutation = useMutation({
    mutationFn: () =>
      enviarCertificado({
        empresa_id: (empresa as INfseEmpresaBusca).empresa_id,
        arquivo: arquivo as File,
        senha,
      }),
    onSuccess: (cert: INfseCertificado) => {
      island.notify(`Certificado guardado · válido até ${dataBR(cert.valido_ate)}`, {
        tone: 'ok',
        meta: 'cifrado',
      });
      setSenha('');
      queryClient.invalidateQueries({ queryKey: ['nfse-certificados'] });
      queryClient.invalidateQueries({ queryKey: ['nfse-emitentes'] });
      queryClient.invalidateQueries({ queryKey: ['nfse-painel'] });
      queryClient.invalidateQueries({ queryKey: ['nfse-ultima-nota'] });
    },
    onError: () => {
      setSenha('');
      island.notify('Certificado não guardado', { tone: 'bad', meta: 'veja ao lado' });
    },
  });

  const recomecar = () => {
    mutation.reset();
    setArquivo(null);
    setSenha('');
  };

  const escolher = (f?: File | null) => {
    if (!f) return;
    mutation.reset();
    setArquivo(f);
  };

  if (mutation.isPending || mutation.isSuccess || mutation.isError) {
    const falhou = mutation.isError ? etapaComErro(mutation.error) : 0;
    const cert = mutation.data;
    const estado = (n: number): CheckItem['state'] => {
      if (mutation.isPending) return n === 1 ? 'running' : 'pending';
      if (mutation.isSuccess) return 'ok';
      if (n < falhou) return 'ok';
      return n === falhou ? 'fail' : 'pending';
    };
    const itens: CheckItem[] = [
      { title: 'Senha conferida', detail: 'pkcs12 aberto com a senha informada', state: estado(1) },
      {
        title: 'CNPJ do certificado',
        detail: `${cnpjMask(cert?.cnpj ?? empresa?.cnpj)} bate com o emitente`,
        state: estado(2),
      },
      {
        title: 'Validade',
        detail: cert ? `até ${dataBR(cert.valido_ate)}` : 'não pode estar vencido',
        state: estado(3),
      },
      { title: 'Cifrado e guardado', detail: 'chave KMS · bucket privado', state: estado(4) },
    ];
    return (
      <Flex direction="column" gap="14px">
        <Text fontSize="13px" color={C.ink2}>
          <b>{empresa?.razao_social}</b>
          <br />
          {arquivo?.name}
        </Text>
        <CheckList items={itens} />
        {mutation.isSuccess && (
          <Banner tone="success" compact>
            Pronto. A empresa já pode emitir e o certificado anterior, se havia, foi desativado.
          </Banner>
        )}
        {mutation.isError && (
          <Banner tone="danger" compact>
            {extractApiErrorMessage(mutation.error, 'Não foi possível guardar o certificado.')}
          </Banner>
        )}
        {!mutation.isPending && (
          <NButton variant="secondary" onClick={recomecar}>
            {mutation.isSuccess ? 'Enviar outro' : 'Tentar de novo'}
          </NButton>
        )}
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="14px">
      <FormControl isRequired>
        <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1}>
          Empresa
        </FormLabel>
        <EmpresaBusca empresa={empresa} emitentes={emitentes} onSelecionar={setEmpresa} />
        <FormHelperText fontSize="11.5px" color={C.subtle}>
          Busque pelo código, CNPJ ou nome. A empresa só passa a emitir depois deste envio.
        </FormHelperText>
      </FormControl>

      <Flex
        as="button"
        type="button"
        direction="column"
        align="center"
        gap={1.5}
        py={5}
        px={3}
        borderRadius="18px"
        borderWidth="1.5px"
        borderStyle="dashed"
        borderColor={arrastando || arquivo ? C.accent : C.lineInput}
        bg={arrastando ? C.brand50 : 'rgba(255, 255, 255, 0.6)'}
        transition="all 0.2s"
        onClick={() => inputRef.current?.click()}
        onDragOver={e => {
          e.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={e => {
          e.preventDefault();
          setArrastando(false);
          escolher(e.dataTransfer.files?.[0]);
        }}
      >
        <IconTile
          icon={arquivo ? CheckCircle : UploadSimple}
          tone={arquivo ? 'success' : 'violet'}
          size={40}
        />
        <Text fontSize="13px" fontWeight={700} color={C.ink}>
          {arquivo ? arquivo.name : 'Clique para escolher o arquivo'}
        </Text>
        <Text fontSize="12px" color={C.subtle}>
          {arquivo
            ? `${(arquivo.size / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB · clique para trocar`
            : 'ou arraste o .pfx para cá'}
        </Text>
      </Flex>
      <input
        ref={inputRef}
        type="file"
        accept=".pfx,.p12,application/x-pkcs12"
        hidden
        onChange={e => escolher(e.target.files?.[0])}
      />

      <FormControl isRequired>
        <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1} htmlFor="cert-senha">
          Senha do certificado
        </FormLabel>
        <InputGroup>
          <Input
            id="cert-senha"
            type={mostrar ? 'text' : 'password'}
            value={senha}
            onChange={e => setSenha(e.target.value)}
            autoComplete="off"
            borderRadius="10px"
            bg="rgba(255, 255, 255, 0.78)"
            borderColor={C.lineInput}
          />
          <InputRightElement>
            <IconButton
              aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
              icon={mostrar ? <EyeSlash size={16} /> : <Eye size={16} />}
              size="sm"
              variant="ghost"
              borderRadius="999px"
              onClick={() => setMostrar(!mostrar)}
            />
          </InputRightElement>
        </InputGroup>
        <FormHelperText fontSize="11.5px" color={C.subtle}>
          Conferida na hora, nunca guardada em texto
        </FormHelperText>
      </FormControl>

      <NButton
        icon={Lock}
        isDisabled={!empresa || !arquivo || !senha}
        onClick={() => mutation.mutate()}
      >
        Enviar certificado
      </NButton>
    </Flex>
  );
}

export function NfseEmissaoCertificadosPage() {
  const { hasPermission, isAdmin } = useAuth();
  const { getPainel, getEmitentes, autorizarProcurador } = useNfseEmissao();
  const queryClient = useQueryClient();
  // Marca a empresa como procuradora de outras: o certificado dela passa a poder assinar
  // notas alheias. Só o Administrador (grupo 1) vê o botão (o backend confere de novo).
  const autorizar = useMutation({
    mutationFn: ({ empresaId, autorizado }: { empresaId: number; autorizado: boolean }) =>
      autorizarProcurador(empresaId, autorizado),
    onSuccess: (r, { autorizado }) => {
      island.notify(autorizado ? 'Procurador autorizado' : 'Autorização removida', {
        tone: 'ok',
        meta: r.razao_social,
      });
      queryClient.invalidateQueries({ queryKey: ['nfse-emitentes'] });
      queryClient.invalidateQueries({ queryKey: ['nfse-procuradores'] });
    },
    onError: erro =>
      island.notify(extractApiErrorMessage(erro, 'Não foi possível alterar a autorização.'), {
        tone: 'bad',
      }),
  });
  const [searchParams] = useSearchParams();
  const [alvo, setAlvo] = useState<INfseEmpresaBusca | null>(null);
  // Aba do card ao lado: o envio do certificado é o padrão; a procuração, a alternativa.
  const [aba, setAba] = useState<'certificado' | 'procuracao'>('certificado');
  const podeEnviar = hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.certificado);

  const ambiente = useQuery({ queryKey: ['nfse-painel', 'mes'], queryFn: getPainel }).data
    ?.ambiente;
  const emitentesQuery = useQuery({ queryKey: ['nfse-emitentes'], queryFn: getEmitentes });
  const emitentes = useMemo(
    () =>
      [...(emitentesQuery.data ?? [])].sort(
        (a, b) =>
          // Quem trava a emissão primeiro: vencido/ausente, depois o que vence antes.
          (a.certificado_dias ?? -9999) - (b.certificado_dias ?? -9999),
      ),
    [emitentesQuery.data],
  );

  // ?emitente= vem da visão geral e das empresas bloqueadas: já abre o envio daquela empresa.
  const doLink = Number(searchParams.get('emitente')) || null;
  useEffect(() => {
    const e = emitentes.find(x => x.id === doLink);
    if (e) {
      setAlvo(paraBusca(e));
      if (e.modo_certificado === 'procuracao') setAba('procuracao');
    }
  }, [doLink, emitentes.length]);

  return (
    <NfsePage wide ambiente={ambiente}>
      <ModuleHeader
        title="Certificados A1"
        icon={ShieldCheck}
        tone="violet"
        description="Usados para assinar e enviar as notas. Ficam cifrados (chave própria por certificado, protegida pelo KMS) no bucket e só são abertos em memória. A senha nunca aparece na tela nem em log."
        crumbs={[
          { label: 'Emissão de NFS-e', to: NFSE_PATHS.painel },
          { label: 'Certificados A1' },
        ]}
        actions={<EnvBadge ambiente={ambiente} />}
      />

      <Grid
        templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 360px' }}
        gap="18px"
        alignItems="start"
      >
        <Card overflow="hidden">
          <Box p="16px 20px" borderBottomWidth="1px" borderColor={C.line}>
            <Text as="h2" fontSize="16px" fontWeight={650}>
              Certificados por empresa
            </Text>
            <Text fontSize="12.5px" color={C.subtle} mt={0.5}>
              Aviso por e-mail 30 dias antes do vencimento
            </Text>
          </Box>
          {emitentesQuery.isLoading ? (
            <Carregando />
          ) : (
            <Tabela
              cabecalho={
                <>
                  <Th>Empresa</Th>
                  <Th>Validade</Th>
                  <Th>Situação</Th>
                  <Th />
                </>
              }
            >
              {emitentes.map((e, i) => {
                const cs = e.certificado_situacao;
                const dias = e.certificado_dias ?? 0;
                const procuracao = e.modo_certificado === 'procuracao' ? e.procurador : null;
                let cor = C.success;
                if (cs === 'a_vencer') cor = C.warning;
                if (cs === 'vencido') cor = C.danger;
                return (
                  <Linha key={e.id} i={i} selecionada={alvo?.emitente_id === e.id}>
                    <Td>
                      <Text fontWeight={600} noOfLines={1}>
                        {e.razao_social}
                      </Text>
                      <SubLinha>
                        {cnpjMask(e.cnpj)}
                        {!procuracao && e.certificado_emissor ? ` · ${e.certificado_emissor}` : ''}
                      </SubLinha>
                      {procuracao && (
                        <SubLinha>
                          Procuração · {procuracao.razao_social}
                          {procuracao.valida_ate ? ` até ${dataBR(procuracao.valida_ate)}` : ''}
                        </SubLinha>
                      )}
                      {e.procurador_autorizado && (
                        <SubLinha>Autorizada como procuradora de outras empresas</SubLinha>
                      )}
                    </Td>
                    <Td minW="160px">
                      {e.certificado_valido_ate ? (
                        <>
                          <Text fontWeight={600} color={cs === 'valido' ? C.ink : cor}>
                            {dataBR(e.certificado_valido_ate)}
                          </Text>
                          <Box mt={1.5} maxW="140px">
                            <Meter value={(Math.max(0, dias) / 365) * 100} color={cor} />
                          </Box>
                        </>
                      ) : (
                        <Text color={C.subtle}>não enviado</Text>
                      )}
                    </Td>
                    <Td>
                      {e.situacao === 'procuracao_vencida' ? (
                        <StatusTag kind="procuracao_vencida" />
                      ) : (
                        <StatusTag kind={cs}>
                          {cs === 'a_vencer' ? `Vence em ${dias} dias` : undefined}
                        </StatusTag>
                      )}
                    </Td>
                    <Td textAlign="right">
                      {podeEnviar && procuracao && (
                        <NButton
                          size="sm"
                          variant={e.situacao === 'liberado' ? 'ghost' : 'secondary'}
                          icon={Handshake}
                          onClick={() => {
                            setAlvo(paraBusca(e));
                            setAba('procuracao');
                          }}
                        >
                          Procuração
                        </NButton>
                      )}
                      {podeEnviar && !procuracao && (
                        <NButton
                          size="sm"
                          variant={cs === 'valido' ? 'ghost' : 'secondary'}
                          icon={UploadSimple}
                          onClick={() => {
                            setAlvo(paraBusca(e));
                            setAba('certificado');
                          }}
                        >
                          {cs === 'ausente' ? 'Enviar' : 'Substituir'}
                        </NButton>
                      )}
                      {isAdmin() && !procuracao && cs !== 'ausente' && (
                        <NButton
                          size="sm"
                          variant="ghost"
                          icon={Handshake}
                          isDisabled={autorizar.isPending}
                          onClick={() =>
                            autorizar.mutate({
                              empresaId: e.empresa_id,
                              autorizado: !e.procurador_autorizado,
                            })
                          }
                        >
                          {e.procurador_autorizado ? 'Remover procurador' : 'Autorizar procurador'}
                        </NButton>
                      )}
                    </Td>
                  </Linha>
                );
              })}
              {emitentes.length === 0 && (
                <Vazio colunas={4}>
                  Nenhuma empresa habilitada ainda. Envie o primeiro certificado ao lado.
                </Vazio>
              )}
            </Tabela>
          )}
        </Card>

        <Card p={5} position={{ xl: 'sticky' }} top={{ xl: '64px' }}>
          <Flex gap={2} mb={4}>
            <NButton
              size="sm"
              variant={aba === 'certificado' ? 'primary' : 'ghost'}
              icon={UploadSimple}
              onClick={() => setAba('certificado')}
            >
              Certificado da empresa
            </NButton>
            <NButton
              size="sm"
              variant={aba === 'procuracao' ? 'primary' : 'ghost'}
              icon={Handshake}
              onClick={() => setAba('procuracao')}
            >
              Procuração
            </NButton>
          </Flex>
          {aba === 'certificado' ? (
            <CardHead title="Enviar certificado" description=".pfx ou .p12 do CNPJ emissor" />
          ) : (
            <CardHead
              title="Emitir por procuração"
              description="Certificado do escritório, com procuração NFS-e no e-CAC"
            />
          )}
          {podeEnviar && aba === 'certificado' && (
            <EnvioCertificado emitentes={emitentes} inicial={alvo} />
          )}
          {podeEnviar && aba === 'procuracao' && (
            <ProcuracaoForm emitentes={emitentes} inicial={alvo} />
          )}
          {!podeEnviar && (
            <Text fontSize="13px" color={C.subtle}>
              Você não tem permissão para enviar certificados.
            </Text>
          )}
        </Card>
      </Grid>
    </NfsePage>
  );
}
